/** @format */

import React from 'react';
import { connect, useDispatch, useSelector } from 'react-redux';
import { Table, TableBody, TableCell, TableFooter, TableHead, TableRow, withStyles } from '@material-ui/core';
import TableHeaderContentCell from '../../../components/Table/TableHeaderContentCell';
import I18n from '../../../components/Translate/I18n';
import { CurrencyHelper } from '../../../helpers/CurrencyHelper';
import { DateHelperTs } from '../../../helpers/DateHelperTs';
import { checkEventForCount } from '../../../helpers/BillingsHelper';
import { CdrReducers } from '../../../modules/cdr/reducers';
import { CdrList } from '../../../interfaces/platform/CdrList';
import Pager from '../../../components/Adescom/PagerRange';
import oplAppStyle from '../../../assets/themes/OPL/jss/App/OplAppStyle';
import Order from '../../../interfaces/forms/Order';
import PaperedTable from '../../../components/Table/PaperedTable';
import CustomTextButtom from '../../../components/Buttons/CustomTextButton';
import { NotificationHelper } from '../../../helpers/NotificationHelper';
import { NotificationToCsv } from '../../../modules/notifications/NotificationToCsv';
import { ItemLoaders } from '../../../core/loaders/ItemLoaders';
import Filter from '../../../interfaces/forms/Filter';
import compose from 'recompose/compose';
import { BillingResultToCsv } from '../../../modules/billing/BillingResultToCsv';
import {Auth} from "../../../modules/auth/Auth";

interface BillingResultListProps {
    classes: any;
    paginationForSearchedBilling: (uri?: string) => void;
    orders: Array<Order>;
    filters: Array<Filter>;
    setOrders: (order: Array<Order>) => void;
    showMainLoader?: any;
}

export const BillingResultList = (props: BillingResultListProps): React.ReactElement => {
    const tableStickyHeader = { root: props.classes.tableStickyHeader };
    const tableCellRoot = { root: props.classes.tableCellRoot };
    const tableRootNotMinWidth = { root: props.classes.tableRootNotMinWidth };
    const { orders, setOrders, filters, showMainLoader } = props;
    const dispatch = useDispatch();

    const billingResult = useSelector((state: { cdrReducers: CdrReducers }) => {
        if (state.cdrReducers && state.cdrReducers.collections) {
            return state.cdrReducers.collections;
        }
        return undefined;
    });

    const getDiscountNameFromLabel = (label: string | null): string | null => {
        if (!label) {
            return label;
        }
        const x = label.split(';');
        if (label.length >= 1) {
            return x[1];
        } else {
            return label;
        }
    };

    const exportToCsv = (): void => {
        if (billingResult && !billingResult['hydra:totalItems']) {
            NotificationHelper.notifyError('general:common.no_data_to_export', dispatch);
            return;
        }
        const toCsv = new BillingResultToCsv(new ItemLoaders(dispatch));
        toCsv.exportToCsv(orders, filters, showMainLoader);
    };

    if (billingResult) {
        return (
            <PaperedTable
                classes={tableRootNotMinWidth}
                beforeTableContent={
                    <CustomTextButtom callback={exportToCsv} callbackParam={{}} color="primary" label="general:common.export_to_csv" size="lg" />
                }
            >
                <TableHead>
                    <TableRow>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.date"
                                updateOrderList={setOrders}
                                fieldName="calldate"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.real_source_number"
                                updateOrderList={setOrders}
                                fieldName="src"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.real_target_number"
                                updateOrderList={setOrders}
                                fieldName="dst"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.source_number"
                                updateOrderList={setOrders}
                                fieldName="realSrc"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.target_number"
                                updateOrderList={setOrders}
                                fieldName="realDst"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.roaming?"
                                updateOrderList={setOrders}
                                fieldName="roaming"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.roaming_country"
                                updateOrderList={setOrders}
                                fieldName="roamingCountry"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.billing_type"
                                updateOrderList={setOrders}
                                fieldName="fractionname"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.billing_type2"
                                updateOrderList={setOrders}
                                fieldName="fractionname2"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.pricelist_mtr_ftr"
                                updateOrderList={setOrders}
                                fieldName="linearBillingName"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.pricelist_host"
                                updateOrderList={setOrders}
                                fieldName="linearBillingName2"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.discount"
                                updateOrderList={setOrders}
                                fieldName="usedDiscountsInfo"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.discount2"
                                updateOrderList={setOrders}
                                fieldName="usedDiscountsInfo2"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        {/*<TableCell classes={tableStickyHeader} align={'left'}>*/}
                        {/*    <TableHeaderContentCell textAlign="left" title="tariffing:h.redirect?" />*/}
                        {/*</TableCell>*/}
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.direction"
                                updateOrderList={setOrders}
                                fieldName="incoming"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.amount_duration"
                                updateOrderList={setOrders}
                                fieldName="billsec"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.amount_duration_no_discount"
                                updateOrderList={setOrders}
                                fieldName="billsecNoDiscount"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.cost_host"
                                titleVariables={{ x: Auth.getCurrencySign() }}
                                updateOrderList={setOrders}
                                fieldName="cost"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.cost_mtr_ftr"
                                titleVariables={{ x: Auth.getCurrencySign() }}
                                updateOrderList={setOrders}
                                fieldName="cost2"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.retail_cost"
                                titleVariables={{ x: Auth.getCurrencySign() }}
                                updateOrderList={setOrders}
                                fieldName="pricePrepaid"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                        <TableCell classes={tableStickyHeader} align={'left'}>
                            <TableHeaderContentCell
                                textAlign="left"
                                title="tariffing:h.cost_no_discount"
                                titleVariables={{ x: Auth.getCurrencySign() }}
                                updateOrderList={setOrders}
                                fieldName="priceNoDiscount"
                                showSort={true}
                                defaultDirection="desc"
                                sort={orders}
                            />
                        </TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {billingResult['hydra:member'].map((item: CdrList) => {
                        return (
                            <TableRow key={item.id}>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {DateHelperTs.format(item.calldate)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.src}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.dst}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.realSrc}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.realDst}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {(item.cn === null || item.cn === 'PL') && item.type !== 7 ? (
                                        <I18n trans={'general:common.No'} />
                                    ) : (
                                        <I18n trans={'general:common.Yes'} />
                                    )}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.cn === null || item.cn === 'PL' ? <I18n trans={'general:common.none'} /> : item.cn}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.fractionname}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.fractionname2}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.linearBillingName}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.linearBillingName2}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.usedDiscountsInfo}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.usedDiscountsInfo2}
                                </TableCell>
                                {/*<TableCell classes={tableCellRoot} align={'left'}>*/}
                                {/*    {(item.type === 6 && <I18n trans={'general:common.Yes'} />) || <I18n trans={'general:common.No'} />}*/}
                                {/*</TableCell>*/}
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.incoming ? <I18n trans={'tariffing:h.incoming'} /> : <I18n trans={'tariffing:h.outgoing'} />}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {checkEventForCount(item.type, item.billsec)}
                                    {item.volDown && ` ( ${item.volDown}  KB )`}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {checkEventForCount(item.type, item.billsecNoDiscount)}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.cost && CurrencyHelper.numericToStandardPln(item.cost)}
                                    {/*{item.cost && (*/}
                                    {/*    <I18n trans={'tariffing:h.with_pln'} valueObject={{ x: CurrencyHelper.numericToStandardPln(item.cost) }} />*/}
                                    {/*)}*/}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.cost2 && CurrencyHelper.numericToStandardPln(item.cost2)}
                                    {/*{item.cost2 && (*/}
                                    {/*    <I18n trans={'tariffing:h.with_pln'} valueObject={{ x: CurrencyHelper.numericToStandardPln(item.cost2) }} />*/}
                                    {/*)}*/}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.pricePrepaid && CurrencyHelper.numericToStandardPln(item.pricePrepaid)}
                                    {/*{item.pricePrepaid && (*/}
                                    {/*    <I18n*/}
                                    {/*        trans={'tariffing:h.with_pln'}*/}
                                    {/*        valueObject={{ x: CurrencyHelper.numericToStandardPln(item.pricePrepaid) }}*/}
                                    {/*    />*/}
                                    {/*)}*/}
                                </TableCell>
                                <TableCell classes={tableCellRoot} align={'left'}>
                                    {item.priceNoDiscount && CurrencyHelper.numericToStandardPln(item.priceNoDiscount)}
                                    {/*{item.priceNoDiscount && (*/}
                                    {/*    <I18n*/}
                                    {/*        trans={'tariffing:h.with_pln'}*/}
                                    {/*        valueObject={{ x: CurrencyHelper.numericToStandardPln(item.priceNoDiscount) }}*/}
                                    {/*    />*/}
                                    {/*)}*/}
                                </TableCell>
                            </TableRow>
                        );
                    })}
                </TableBody>
                <TableFooter>
                    <TableCell classes={tableCellRoot} colSpan={3}>
                        {billingResult && billingResult['hydra:view'] && (
                            <Pager callback={props.paginationForSearchedBilling} views={billingResult} />
                        )}
                    </TableCell>
                </TableFooter>
            </PaperedTable>
        );
    } else {
        return <div></div>;
    }
};

export default compose<any, any>(connect(), withStyles(oplAppStyle))(BillingResultList);
